import React from 'react';
import { Button } from '@/components/ui/button';
import { heroData } from '@/data/mock';
import { Check, Play, BarChart3, Gauge, TrendingUp, User } from 'lucide-react';

export const HeroSection = ({ onBookDemo, onSeeSimulation }) => {
  return (
    <section className="relative min-h-screen pt-32 pb-20 overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 bg-slate-950" />
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-indigo-500/10 rounded-full blur-3xl" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left content */}
          <div className="text-left">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              {heroData.headline}
            </h1>
            <p className="text-lg sm:text-xl text-slate-400 mb-8 max-w-xl">
              {heroData.subheadline}
            </p>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <Button
                onClick={onBookDemo}
                size="lg"
                className="bg-gradient-to-r from-cyan-500 to-indigo-500 hover:from-cyan-400 hover:to-indigo-400 text-white border-0 shadow-lg shadow-cyan-500/25 text-base px-8 py-6"
              >
                {heroData.primaryCTA}
              </Button>
              <Button
                onClick={onSeeSimulation}
                size="lg"
                variant="outline"
                className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white text-base px-8 py-6"
              >
                <Play size={18} className="mr-2" />
                {heroData.secondaryCTA}
              </Button>
            </div>
            
            {/* Micro-proofs */}
            <div className="flex flex-wrap gap-3">
              {heroData.microProofs.map((proof, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-full text-sm text-slate-300"
                >
                  <Check size={14} className="text-cyan-400" />
                  {proof}
                </div>
              ))}
            </div>
          </div>
          
          {/* Right - Mock UI */}
          <div className="relative lg:pl-8">
            <div className="relative">
              {/* Glow effect */}
              <div className="absolute -inset-4 bg-gradient-to-r from-cyan-500/20 to-indigo-500/20 rounded-2xl blur-xl" />
              
              {/* Main card */}
              <div className="relative bg-slate-900/90 border border-slate-700/50 rounded-2xl p-6 backdrop-blur-sm">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-400 to-indigo-500 flex items-center justify-center">
                      <BarChart3 size={20} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Critical Task Simulation</h3>
                      <p className="text-slate-400 text-sm">Security Analyst Role</p>
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-medium rounded-full">
                    Completed
                  </span>
                </div>
                
                {/* Skill Map Preview */}
                <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/30">
                  <div className="flex items-center gap-2 mb-4">
                    <Gauge size={16} className="text-cyan-400" />
                    <span className="text-white font-medium text-sm">Skill Map Analysis</span>
                  </div>
                  
                  {/* Skills bars */}
                  <div className="space-y-4">
                    {[
                      { skill: 'Threat Detection', score: 92 },
                      { skill: 'Incident Response', score: 87 },
                      { skill: 'Log Analysis', score: 78 },
                      { skill: 'Communication', score: 85 },
                    ].map((item, index) => (
                      <div key={index}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-300">{item.skill}</span>
                          <span className="text-cyan-400 font-medium">{item.score}%</span>
                        </div>
                        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-cyan-500 to-indigo-500 rounded-full transition-all duration-1000"
                            style={{ width: `${item.score}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Bottom stats */}
                <div className="flex items-center justify-between mt-5 pt-5 border-t border-slate-700/50">
                  <div className="flex items-center gap-2">
                    <User size={16} className="text-slate-400" />
                    <span className="text-slate-300 text-sm">Verified Identity</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <TrendingUp size={16} className="text-emerald-400" />
                    <span className="text-emerald-400 text-sm font-medium">Top 15% of candidates</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
